/**
 * PickDetailModal Component
 * Full-screen modal (mobile) / large centered modal (desktop)
 * Shows detailed pick information across 10 sections
 */

import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type { PickDetailRecord, PicksFilters } from '@server/services/admin/adminPicksTypes.ts';
import { format } from 'date-fns';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface PickDetailModalProps {
  pickId: string;
  onClose: () => void;
  /** Shows Picks Data narrowed to this pick's parlay or generation. */
  onShowLinkedPicks: (filters: PicksFilters) => void;
}

export function PickDetailModal({ pickId, onClose, onShowLinkedPicks }: PickDetailModalProps) {
  const [pick, setPick] = useState<PickDetailRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configExpanded, setConfigExpanded] = useState(false);

  useEffect(() => {
    async function fetchPickDetail() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`/api/admin/picks/${pickId}`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch pick: ${response.statusText}`);
        }

        const result: PickDetailRecord = await response.json();
        setPick(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch pick');
      } finally {
        setLoading(false);
      }
    }

    fetchPickDetail();
  }, [pickId]);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-4xl rounded-lg border border-theme-border bg-theme-surface p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-theme-border"></div>
            <div className="h-4 w-full rounded bg-theme-border"></div>
            <div className="h-4 w-3/4 rounded bg-theme-border"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !pick) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-md rounded-lg border border-theme-border bg-theme-surface p-8">
          <p className="text-theme-danger">{error || 'Pick not found'}</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 rounded-lg bg-theme-primary-fill px-4 py-2 text-theme-on-primary-fill hover:bg-theme-primary-fill/90"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="w-full max-w-5xl rounded-lg border border-theme-border bg-theme-bg">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-theme-border p-6">
            <div>
              <h2 className="text-2xl font-bold text-theme-text">{pick.playerName || pick.team}</h2>
              <p className="text-sm text-theme-text-secondary">
                {formatStatLabel(pick.propType)} {pick.overUnder} {pick.line}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 hover:bg-theme-surface"
            >
              <X className="h-5 w-5 text-theme-text" />
            </button>
          </div>

          <div className="max-h-[calc(100vh-200px)] space-y-6 overflow-y-auto p-6">
            {/* Section 1: Core Pick Info */}
            <Section title="Core Pick Info">
              <InfoGrid>
                <InfoItem label="Line" value={pick.line || '-'} />
                <InfoItem label="Predicted" value={pick.predictedValue || '-'} />
                <InfoItem label="Edge %" value={pick.edgePct ? `${pick.edgePct}%` : '-'} />
                <InfoItem label="Odds" value={pick.odds || '-'} />
                <InfoItem label="Sportsbook" value={pick.sportsbook || '-'} />
                <InfoItem label="Market Key" value={pick.marketKey || '-'} />
                <InfoItem label="Team" value={pick.team} />
                <InfoItem label="Opponent" value={pick.opponent || '-'} />
                <InfoItem label="Is Home" value={pick.isHome ? 'Yes' : 'No'} />
              </InfoGrid>
            </Section>

            {/* Section 2: AI Analysis */}
            <Section title="AI Analysis">
              <InfoGrid>
                <InfoItem label="AI Edge" value={pick.aiEdge || '-'} />
                <InfoItem label="AI Confidence" value={pick.aiConfidence || '-'} />
                <InfoItem label="AI Trend" value={pick.aiTrend || '-'} />
                <InfoItem label="AI Tier" value={pick.aiTier || '-'} />
              </InfoGrid>
              {pick.aiReasoning && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-theme-text-secondary">AI Reasoning:</p>
                  <p className="mt-1 text-sm text-theme-text">{pick.aiReasoning}</p>
                </div>
              )}
            </Section>

            {/* Section 3: Scoring Breakdown */}
            <Section title="Scoring Breakdown">
              <InfoGrid>
                <InfoItem label="Strategy Score" value={pick.strategyScore || '-'} />
                <InfoItem label="Prediction Score" value={pick.predictionScore || '-'} />
                <InfoItem
                  label="Strategy Recommendation"
                  value={pick.strategyRecommendation || '-'}
                />
                <InfoItem
                  label="Prediction Recommendation"
                  value={pick.predictionRecommendation || '-'}
                />
                <InfoItem
                  label="Score Reliability"
                  value={pick.scoreReliability?.toString() || '-'}
                />
              </InfoGrid>
              {pick.conflictsJson && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-theme-text-secondary">Conflicts:</p>
                  <pre className="mt-1 overflow-x-auto rounded bg-theme-surface p-2 text-xs text-theme-text">
                    {JSON.stringify(pick.conflictsJson, null, 2)}
                  </pre>
                </div>
              )}
            </Section>

            {/* Section 4: Game Context */}
            <Section title="Game Context">
              <InfoGrid>
                <InfoItem
                  label="Game Date"
                  value={pick.gameDate ? format(new Date(pick.gameDate), 'PPp') : '-'}
                />
                <InfoItem label="Home Spread" value={pick.homeSpread || '-'} />
                <InfoItem label="Game Total" value={pick.gameTotal || '-'} />
                <InfoItem label="Pace" value={pick.combinedPace || '-'} />
                <InfoItem label="Blowout Risk" value={pick.blowoutRisk || '-'} />
                <InfoItem label="Player B2B" value={pick.isB2b ? 'Yes' : 'No'} />
                <InfoItem label="Opponent B2B" value={pick.opponentB2b ? 'Yes' : 'No'} />
              </InfoGrid>
            </Section>

            {/* Section 5: Player Stats Snapshot */}
            {pick.playerName && (
              <Section title="Player Stats Snapshot">
                <InfoGrid>
                  <InfoItem label="L5 Avg" value={pick.l5Avg || '-'} />
                  <InfoItem label="L10 Avg" value={pick.l10Avg || '-'} />
                  <InfoItem label="L20 Avg" value={pick.l20Avg || '-'} />
                  <InfoItem label="Season Avg" value={pick.seasonAvg || '-'} />
                  <InfoItem label="Vs Team Avg" value={pick.vsTeamAvg || '-'} />
                </InfoGrid>
              </Section>
            )}

            {/* Section 6: Advanced Stats */}
            <Section title="Advanced Stats">
              <InfoGrid>
                <InfoItem label="True Shooting (0 to 1)" value={pick.trueShootingFrac || '-'} />
                <InfoItem label="Assist/TO Ratio" value={pick.assistTurnoverRatio || '-'} />
                <InfoItem
                  label="Poss. Actions / 48 (L10)"
                  value={pick.possActionsPer48L10 || '-'}
                />
                <InfoItem label="Team Off Rating" value={pick.teamOffRating || '-'} />
                <InfoItem label="Team Def Rating" value={pick.teamDefRating || '-'} />
                <InfoItem label="Weighted Baseline" value={pick.weightedBaseline || '-'} />
                <InfoItem label="Vs Team Adjustment" value={pick.vsTeamAdjustment || '-'} />
              </InfoGrid>
            </Section>

            {/* Section 7: Direction Bias Info */}
            {pick.directionWasFlipped && (
              <Section title="Direction Validation">
                <InfoGrid>
                  <InfoItem label="Was Flipped" value="Yes" />
                  <InfoItem label="Original Direction" value={pick.originalDirection || '-'} />
                  <InfoItem label="Direction Preference" value={pick.directionPreference || '-'} />
                  <InfoItem label="Final Direction" value={pick.betDirection || '-'} />
                </InfoGrid>
              </Section>
            )}

            {/* Section 8: Config Snapshot */}
            <Section title="Analysis Config">
              <button
                type="button"
                onClick={() => setConfigExpanded(!configExpanded)}
                className="flex items-center space-x-2 text-sm text-theme-primary hover:underline"
              >
                <span>{configExpanded ? 'Hide' : 'Show'} Config JSON</span>
                {configExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>
              {configExpanded && pick.analysisConfig !== null && (
                <pre className="mt-2 overflow-x-auto rounded bg-theme-surface p-4 text-xs text-theme-text">
                  {JSON.stringify(pick.analysisConfig as Record<string, unknown>, null, 2)}
                </pre>
              )}
            </Section>

            {/* Section 9: Links */}
            <Section title="Links">
              <div className="flex flex-wrap gap-2">
                {pick.parlayId !== null && (
                  <button
                    type="button"
                    onClick={() => onShowLinkedPicks({ parlayId: pick.parlayId ?? undefined })}
                    className="text-sm text-theme-primary hover:underline"
                  >
                    View Parlay
                  </button>
                )}
                {pick.generationId && (
                  <button
                    type="button"
                    onClick={() =>
                      onShowLinkedPicks({ generationId: pick.generationId ?? undefined })
                    }
                    className="text-sm text-theme-primary hover:underline"
                  >
                    View Generation
                  </button>
                )}
              </div>
            </Section>

            {/* Section 10: Outcome */}
            <Section title="Outcome">
              <InfoGrid>
                <InfoItem label="Resolved" value={pick.outcomeResolved ? 'Yes' : 'No'} />
                <InfoItem
                  label="Result"
                  value={
                    pick.hit === true
                      ? 'HIT'
                      : pick.hit === false
                        ? 'MISS'
                        : pick.hit === null && pick.outcomeResolved
                          ? 'PUSH'
                          : 'PENDING'
                  }
                />
                <InfoItem label="Actual Value" value={pick.actualValue || '-'} />
                <InfoItem label="Margin" value={pick.margin || '-'} />
                <InfoItem label="Was Tracked" value={pick.wasTracked ? 'Yes' : 'No'} />
                <InfoItem label="Was Bet On" value={pick.wasBetOn ? 'Yes' : 'No'} />
              </InfoGrid>
            </Section>

            {/* Metadata */}
            <Section title="Metadata">
              <InfoGrid>
                <InfoItem label="Pick ID" value={pick.id} />
                <InfoItem label="Analysis Mode" value={pick.analysisMode || '-'} />
                <InfoItem label="Model Version" value={pick.modelVersion || '-'} />
                <InfoItem label="Prompt Version" value={pick.promptVersion || '-'} />
                <InfoItem label="Config Variation" value={pick.configVariation || '-'} />
                <InfoItem label="Created At" value={format(new Date(pick.createdAt), 'PPp')} />
              </InfoGrid>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div className="rounded-lg border border-theme-border bg-theme-surface p-4">
      <h3 className="mb-3 text-sm font-bold text-theme-text">{title}</h3>
      {children}
    </div>
  );
}

function InfoGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-4 md:grid-cols-3">{children}</div>;
}

interface InfoItemProps {
  label: string;
  value: string;
}

function InfoItem({ label, value }: InfoItemProps) {
  return (
    <div>
      <p className="text-xs font-medium text-theme-text-secondary">{label}</p>
      <p className="mt-1 text-sm text-theme-text">{value}</p>
    </div>
  );
}
